import { createDefaultHeaderFooter } from '../types/document-header-footer'
import type { PlacedBlock } from '../types/placed-block'
import { FormTemplateTypeEnum } from '../../../modules/toolsApi/forms/form-templates/form-template-item-type.enum'

// Tipos v1 que viram 'label' em v2 (conteúdo HTML/texto em content[0])
const LABEL_TYPES = new Set(['label', 'label_formated', 'text_formated'])

// Tipos v1 com conteúdo de opções (content[] = lista de opções)
const CHOICE_TYPES = new Set(['choice_one', 'choice_multiple'])

// Mapeamento direto de tipo v1 → tipo v2. "sign" não entra aqui — é tratado à parte em
// convertV1ToV2 (vira a config de assinatura do documento, não um bloco).
const TYPE_MAP: Record<string, string> = {
    text_short: 'text_short',
    text_long: 'text_long',
    date: 'date',
    before_after: 'before_after',
    choice_one: 'choice_one',
    choice_multiple: 'choice_multiple',
    label: 'label',
    label_formated: 'label',
    text_formated: 'label',
    image_paint: 'image_annotation',
    image_gallery: 'image_annotation',
    fitzpatrick: 'fitzpatrick_scale',
    hair_stroke: 'hair_stroke',
}

function buildData(v1Type: string, content: string[], another: boolean): Record<string, any> {
    if (LABEL_TYPES.has(v1Type)) return { text: content?.[0] ?? '' }
    // O v1 não tinha rótulo configurável pro campo livre — sempre "Outro".
    if (CHOICE_TYPES.has(v1Type)) return { options: content ?? [], allowOther: another ?? false, otherLabel: 'Outro' }
    return {}
}

export interface V1Template {
    title: string
    // Mesmo enum FormTemplateTypeEnum usado no v2 (category) — valores compatíveis, sem tradução.
    type?: string
    form: Array<{
        id: string
        label: string
        type: string
        required: boolean
        enabled: boolean
        another: boolean
        content: string[]
        locked: boolean
    }>
}

export interface ConvertedTemplate {
    title: string
    category: string
    blocks: PlacedBlock[]
    headerFooter: ReturnType<typeof createDefaultHeaderFooter>
    skipped: string[]
}

export function convertV1ToV2(v1: V1Template): ConvertedTemplate {
    const blocks: PlacedBlock[] = []
    const skipped: string[] = []
    const headerFooter = createDefaultHeaderFooter()

    for (const item of v1.form ?? []) {
        // O bloco de assinatura do v1 vira a configuração de assinatura do documento em v2 (Propriedades
        // > "Solicitar assinatura ao preencher"), não um bloco arrastável — então não é importado como
        // campo. Modalidade padrão: link simples (o usuário pode trocar depois em Propriedades).
        if (item.type === 'sign') {
            headerFooter.signature = { requested: true, mode: 1 }
            continue
        }

        const v2Type = TYPE_MAP[item.type]
        if (!v2Type) {
            skipped.push(`"${item.label}" (tipo ${item.type})`)
            continue
        }

        blocks.push({
            id: crypto.randomUUID(),
            type: v2Type,
            label: item.label ?? '',
            data: buildData(item.type, item.content, item.another),
            required: item.required ?? false,
            readOnlyForPublic: item.locked ?? false,
            width: 3,
        })
    }

    return {
        title: v1.title ?? '',
        category: v1.type && v1.type in FormTemplateTypeEnum ? v1.type : FormTemplateTypeEnum.outro,
        blocks,
        headerFooter,
        skipped,
    }
}
